import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { ChevronDown, ChevronRight } from 'lucide-preact';
import { api, type Dataset } from '@/lib/teaching/teacher-api';
import { useAssessmentId, teachHref, invalidateDataset, refreshAssessments } from '@/lib/teaching/state';
import {
  errText, waitForJob, bulkBlock, paperBlock, markText, PAPER_STATUS, CONF_STYLE,
  type Estimate, type Job, type Queue, type QueuePaper, type QueueCell, type ScansView,
} from '@/lib/teaching/slice2';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { Pill, SetupPage, Note, Tabs, NoAiNote } from '@/components/teaching/setup';
import { useAiReady, aiBlockText } from '@/lib/teaching/ai-ready';
import { PdfPages } from '@/components/teaching/PdfPages';

// Review queue (§07, SOP §25, §27, §50): exception-based approval of AI
// proposals. LOW cells always need a per-cell decision; full-marks papers
// must be opened, and spot checks ticked, before "Approve all".

type Ctx = { a: string; q: Queue; codes: Map<string, string>; files: Map<string, string>; reload: () => Promise<void> };

export function Review() {
  const a = useAssessmentId();
  const [q, setQ] = useState<Queue | null>(null);
  const [scans, setScans] = useState<ScansView | null>(null);
  const [codes, setCodes] = useState<Map<string, string>>(new Map());
  const [err, setErr] = useState<string | null>(null);
  const [cls, setCls] = useState('all');
  const [open, setOpen] = useState<string | null>(null);
  const [exPapers, setExPapers] = useState<Set<string>>(new Set());
  const [exCells, setExCells] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    if (!a) return;
    const [qq, ss] = await Promise.all([api<Queue>('GET', `/api/t/assessments/${a}/queue`), api<ScansView>('GET', `/api/t/assessments/${a}/scans`)]);
    setQ(qq); setScans(ss);
  };
  useEffect(() => {
    setQ(null); setErr(null);
    if (!a) return;
    void reload().catch((e) => setErr(errText(e)));
    void api<Dataset>('GET', `/api/t/assessments/${a}/dataset`).then((ds) => setCodes(new Map(ds.misconceptions.map((m) => [m.id, m.code])))).catch(() => undefined);
  }, [a]);
  const changed = async () => { if (a) { invalidateDataset(a); void refreshAssessments(); } await reload(); };

  async function openPaper(p: QueuePaper) {
    if (open === p.paper_id) { setOpen(null); return; }
    setOpen(p.paper_id);
    if (!p.viewed) {
      try { await api('POST', `/api/t/papers/${p.paper_id}/viewed`); await reload(); } catch (e) { setErr(errText(e)); }
    }
  }

  const classes = q ? Array.from(new Map(q.papers.map((p) => [p.class_id, p.class_name]))).map(([id, label]) => ({ id, label })) : [];
  const papers = q?.papers.filter((p) => cls === 'all' || p.class_id === cls) ?? [];
  const files = new Map((scans?.batches ?? []).map((b) => [b.id, b.file_sha256]));
  const reconciled = new Set((scans?.batches ?? []).filter((b) => b.reconciliation.status === 'reconciled').map((b) => b.id));

  // Approve all (except): eligible pending cells on unblocked, not-excluded papers.
  const bulk = papers.flatMap((p) => (paperBlock(p) || exPapers.has(p.paper_id) ? [] : p.cells
    .filter((c) => c.proposal && !bulkBlock(p, c.proposal) && !exCells.has(c.proposal.id)).map((c) => ({ p, id: c.proposal!.id }))));
  const pendingPapers = papers.filter((p) => p.cells.some((c) => c.proposal?.status === 'pending'));
  const heldPapers = pendingPapers.filter((p) => paperBlock(p));
  const lowCells = papers.reduce((s, p) => s + p.cells.filter((c) => c.proposal?.status === 'pending' && c.proposal.confidence === 'low').length, 0);
  const excludedCount = exPapers.size + exCells.size;

  async function approveAll() {
    if (!a || !bulk.length) return;
    setErr(null); setMsg(null); setBusy(true);
    const revs = Object.fromEntries(bulk.map(({ p }) => [p.student_id!, p.enrolment!.revision]));
    try {
      const r = await api<{ approved: number }>('POST', `/api/t/assessments/${a}/approve`, { mode: 'bulk', proposalIds: bulk.map((x) => x.id), expectedRowRevisions: revs });
      setMsg(`Approved ${r.approved} mark${r.approved === 1 ? '' : 's'}.`);
      setExCells(new Set()); setExPapers(new Set());
      await changed();
    } catch (e) { setErr(errText(e)); await reload(); }
    setBusy(false);
  }

  const ctx: Ctx | null = a && q ? { a, q, codes, files, reload: changed } : null;
  return (
    <SetupPage a={a} title="Review queue" actions={a && <Link href={teachHref('/setup/identity', { a })} class="h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[13px] grid place-items-center">Identity check</Link>}>
      <ErrorLine error={err} />
      {!q && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {ctx && q && (
        <>
          <StartMarking ctx={ctx} papers={papers.filter((p) => p.mapping_status === 'confirmed' && !p.quarantined && reconciled.has(p.batch_id)
            && (p.status === 'IDENTITY_VERIFIED' || p.cells.some((c) => c.proposal?.status === 'pending' && c.proposal.stale)))} />
          {classes.length > 1 && <Tabs label="Class" value={cls} onChange={setCls} options={[{ id: 'all', label: 'All' }, ...classes]} />}
          {papers.length === 0 && (
            <Card title="Nothing to review yet">
              <div class="text-[13px] text-[var(--color-text-muted)]">No scans yet for this test. Upload a PDF on <Link href={teachHref('/setup/scans', { a: ctx.a })} class="text-[var(--color-accent)]">Scans and upload</Link> and the AI's marks will wait here for you to check. Marks entered by hand go straight in and skip this queue.</div>
            </Card>
          )}
          {papers.length > 0 && pendingPapers.length === 0 && !msg && <Note tone="ok">All caught up: no marks are waiting for your check.</Note>}
          {(pendingPapers.length > 0 || !!msg) && <Card title="Approve marks" sub="High-confidence marks can be approved together. Medium ones are flagged for a look; low ones need your decision one by one.">
            <div class="flex flex-wrap items-center gap-2">
              {pendingPapers.length > 0 && <Btn kind="primary" disabled={busy || bulk.length === 0} onClick={approveAll}>
                {excludedCount ? `Approve all except ${excludedCount} (${bulk.length} marks)` : `Approve all (${bulk.length} marks)`}
              </Btn>}
              {excludedCount > 0 && <Btn onClick={() => { setExCells(new Set()); setExPapers(new Set()); }}>Clear exclusions</Btn>}
            </div>
            <ul class="mt-2 text-[12px] text-[var(--color-text-muted)] flex flex-col gap-0.5">
              {lowCells > 0 && <li>{lowCells} low-confidence mark{lowCells === 1 ? '' : 's'} left out: open the paper and decide each one.</li>}
              {heldPapers.map((p) => <li key={p.paper_id}>Paper {p.scan_number} ({p.student_name ?? 'no student'}) held back: {paperBlock(p)}.</li>)}
              {excludedCount > 0 && <li>Excluded by you: {exPapers.size} paper{exPapers.size === 1 ? '' : 's'}, {exCells.size} cell{exCells.size === 1 ? '' : 's'}.</li>}
            </ul>
            {msg && <div class="mt-2"><Note tone="ok">{msg}</Note></div>}
          </Card>}

          <div class="md:hidden flex flex-col gap-2">
            {papers.map((p) => (
              <div key={p.paper_id} class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                <div class="p-3 flex flex-col gap-1.5" data-paper-card={p.scan_number} onClick={() => openPaper(p)}>
                  <div class="flex items-center gap-2">
                    <b class="text-[15px] text-[var(--color-text)]">{p.scan_number}</b>
                    <span class="text-[13px] text-[var(--color-text)] truncate">{p.student_name ?? 'identity not confirmed'}</span>
                    <span class="ml-auto text-[13px] tabular-nums text-[var(--color-text)]">{p.ai_marked || p.status === 'APPROVED' ? `${p.proposed_total}/${p.max_total}` : '-'}</span>
                  </div>
                  <div class="flex flex-wrap items-center gap-1.5">
                    <Pill {...PAPER_STATUS[p.status]} />
                    {p.lowest_confidence && <Pill {...CONF_STYLE[p.lowest_confidence]} />}
                    {p.full_marks && <Pill label="Full marks" bg="#e8effb" />}
                    {p.viewed && <span class="text-[11px] text-[var(--color-text-faint)]">opened</span>}
                    <label class="ml-auto text-[11.5px] text-[var(--color-text-muted)] flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={exPapers.has(p.paper_id)} disabled={!p.cells.some((c) => c.proposal?.status === 'pending')}
                        onChange={() => { const s = new Set(exPapers); if (s.has(p.paper_id)) s.delete(p.paper_id); else s.add(p.paper_id); setExPapers(s); }} />exclude
                    </label>
                  </div>
                  {paperBlock(p) && <div class="text-[11px] text-[#b45309]">{paperBlock(p)}</div>}
                </div>
                {open === p.paper_id && <PaperDetail ctx={ctx} p={p} exCells={exCells} setExCells={setExCells} />}
              </div>
            ))}
          </div>

          {papers.length > 0 && <div class="hidden md:block"><Card class="!p-0 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-[12.5px] border-collapse min-w-[720px]">
                <thead>
                  <tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)] bg-[var(--color-elevated)]">
                    <th class="p-2 w-8" /><th class="p-2">Scan</th><th class="p-2">Student</th><th class="p-2">Raw mark</th><th class="p-2">Confidence</th>
                    <th class="p-2">Needs a look</th><th class="p-2">Status</th><th class="p-2">Exclude</th>
                  </tr>
                </thead>
                <tbody>
                  {papers.map((p) => {
                    const isOpen = open === p.paper_id;
                    const block = paperBlock(p);
                    const reviewItems = p.cells.filter((c) => c.proposal?.status === 'pending' && (c.proposal.confidence !== 'high' || c.proposal.stale || c.proposal.teacher_changed))
                      .map((c) => q.items.find((i) => i.id === c.item_id)?.label).filter(Boolean);
                    return (
                      <Fragment key={p.paper_id}>
                        <tr class={'border-t border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-elevated)] ' + (isOpen ? 'bg-[var(--color-elevated)]' : '')}
                          data-paper={p.scan_number} onClick={() => openPaper(p)}>
                          <td class="p-2 text-[var(--color-text-faint)]">{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                          <td class="p-2 font-semibold text-[var(--color-text)]">{p.scan_number}</td>
                          <td class="p-2 text-[var(--color-text)]">
                            {p.student_name ?? <span class="text-[var(--color-text-muted)]">identity not confirmed</span>}
                            <div class="text-[11px] text-[var(--color-text-faint)]">{p.class_name}{p.viewed ? ' · opened' : ''}</div>
                          </td>
                          <td class="p-2 tabular-nums text-[var(--color-text)]">{p.ai_marked || p.status === 'APPROVED' ? `${p.proposed_total}/${p.max_total}` : '-'}{p.full_marks && <span class="ml-1"><Pill label="Full marks" bg="#e8effb" /></span>}</td>
                          <td class="p-2">{p.lowest_confidence ? <Pill {...CONF_STYLE[p.lowest_confidence]} /> : <span class="text-[var(--color-text-faint)]">-</span>}</td>
                          <td class="p-2 text-[var(--color-text)]">{reviewItems.length ? reviewItems.join(', ') : <span class="text-[var(--color-text-faint)]">-</span>}{block && <div class="text-[11px] text-[#b45309]">{block}</div>}</td>
                          <td class="p-2"><Pill {...PAPER_STATUS[p.status]} /></td>
                          <td class="p-2" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" aria-label={`Exclude paper ${p.scan_number}`} checked={exPapers.has(p.paper_id)} disabled={!p.cells.some((c) => c.proposal?.status === 'pending')}
                              onChange={() => { const s = new Set(exPapers); if (s.has(p.paper_id)) s.delete(p.paper_id); else s.add(p.paper_id); setExPapers(s); }} />
                          </td>
                        </tr>
                        {isOpen && (
                          <tr><td colSpan={8} class="p-0 border-t border-[var(--color-border)]">
                            <PaperDetail ctx={ctx} p={p} exCells={exCells} setExCells={setExCells} />
                          </td></tr>
                        )}
                      </Fragment>
                    );
                  })}
                  
                </tbody>
              </table>
            </div>
          </Card></div>}
        </>
      )}
    </SetupPage>
  );
}

// ── Start AI marking: dry-run estimate first, then the job ─────────────

function StartMarking({ ctx, papers }: { ctx: Ctx; papers: QueuePaper[] }) {
  const [est, setEst] = useState<Estimate | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const running = job && (job.status === 'queued' || job.status === 'running');
  const ai = useAiReady();
  if (!papers.length && !job && !err) return null;
  const ids = papers.map((p) => p.paper_id);

  async function estimate() {
    setErr(null);
    try { setEst((await api<{ estimate: Estimate }>('POST', `/api/t/assessments/${ctx.a}/jobs`, { paperIds: ids, dryRun: true })).estimate); }
    catch (e) { setErr(errText(e)); }
  }
  async function start() {
    setErr(null); setEst(null);
    try {
      const r = await api<{ job: Job }>('POST', `/api/t/assessments/${ctx.a}/jobs`, { paperIds: ids });
      setJob(r.job);
      const done = await waitForJob(r.job.id, setJob);
      if (done.status === 'failed') setErr(`Marking failed: ${done.error ?? 'unknown error'}`);
      await ctx.reload();
      setJob(null);
    } catch (e) { setErr(errText(e)); setJob(null); }
  }

  return (
    <Card title="AI marking" sub={`${papers.length} confirmed paper${papers.length === 1 ? '' : 's'} ready to mark`}>
      <div class="flex flex-wrap items-center gap-2">
        {ai === false && !running && <><Btn kind="primary" disabled title={aiBlockText()}>{`Start AI marking (${papers.length})`}</Btn><NoAiNote /></>}
        {!est && ai !== false && <Btn kind="primary" disabled={!!running || !papers.length || ai === undefined} onClick={estimate}>{running ? `Marking… (${job!.status})` : `Start AI marking (${papers.length})`}</Btn>}
        {est && (
          <>
            <span class="text-[12.5px] text-[var(--color-text)]">
              {est.papers} papers · {est.pages} pages · {est.calls} AI call{est.calls === 1 ? '' : 's'} · about {Math.round(est.approx_input_tokens / 1000)}k input tokens
            </span>
            <Btn kind="primary" onClick={start}>Start</Btn>
            <Btn onClick={() => setEst(null)}>Cancel</Btn>
          </>
        )}
      </div>
      <ErrorLine error={err} />
    </Card>
  );
}

// ── One paper: cells beside its pages ─────────────────────────────────

function PaperDetail({ ctx, p, exCells, setExCells }: { ctx: Ctx; p: QueuePaper; exCells: Set<string>; setExCells: (s: Set<string>) => void }) {
  const fileKey = ctx.files.get(p.batch_id);
  return (
    <div class="grid gap-3 p-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] bg-[var(--color-elevated)]">
      <div class="flex flex-col gap-2 min-w-0">
        {!p.student_id && <Note tone="warn">Confirm this paper's identity before approving anything. <Link href={teachHref('/setup/identity', { a: ctx.a })} class="underline">Identity check</Link></Note>}
        {p.enrolment && p.enrolment.attendance !== 'present' && <Note tone="warn">Attendance is {p.enrolment.attendance}; set present on the identity screen first.</Note>}
        {p.cells.map((c) => <CellRow key={c.item_id + (c.proposal?.id ?? '') + (c.result?.revision ?? '')} ctx={ctx} p={p} c={c} exCells={exCells} setExCells={setExCells} />)}
      </div>
      <div class="min-w-0 max-h-[720px] overflow-y-auto">
        {fileKey && p.first_page !== null && p.last_page !== null
          ? <PdfPages batchId={p.batch_id} fileKey={fileKey} first={p.first_page} last={p.last_page} width={420} />
          : <div class="text-[12px] text-[var(--color-text-muted)]">No pages.</div>}
      </div>
    </div>
  );
}

function parseEdit(t: string): { mark: number | null; unavailableReason?: string; blank?: boolean } | null {
  const s = t.trim().toLowerCase();
  if (s === 'b') return { mark: 0, blank: true };
  if (s === 'u') return { mark: null, unavailableReason: 'unreadable' };
  if (s === 'm') return { mark: null, unavailableReason: 'page missing' };
  return /^\d+$/.test(s) ? { mark: Number(s) } : null;
}

function CellRow({ ctx, p, c, exCells, setExCells }: { ctx: Ctx; p: QueuePaper; c: QueueCell; exCells: Set<string>; setExCells: (s: Set<string>) => void }) {
  const item = ctx.q.items.find((i) => i.id === c.item_id)!;
  const pr = c.proposal;
  const pending = pr?.status === 'pending' ? pr : null;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(pending ? markText(pending) === 'blank' ? 'b' : pending.mark !== null ? String(pending.mark) : '' : c.result?.mark?.toString() ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const verified = !!p.student_id && p.enrolment?.identity_status === 'verified' && p.enrolment.attendance === 'present';
  const revs = p.student_id && p.enrolment ? { [p.student_id]: p.enrolment.revision } : {};

  async function send(body: Record<string, unknown>) {
    setErr(null); setBusy(true);
    try { await api('POST', `/api/t/assessments/${ctx.a}/approve`, { mode: 'cells', expectedRowRevisions: revs, ...body }); await ctx.reload(); }
    catch (e) { setErr(errText(e)); setBusy(false); }
  }
  async function saveEdit() {
    const v = parseEdit(text);
    if (!v) { setErr('Type a mark, or b blank, u unreadable, m page missing'); return; }
    await send({ edits: [{ paperId: p.paper_id, itemId: c.item_id, ...v, evidence: pending?.evidence ?? null, codes: v.mark === null || v.blank ? [] : pending?.codes ?? [] }] });
  }
  async function tick() {
    setErr(null);
    try { await api('POST', `/api/t/proposals/${pending!.id}/spotcheck`); await ctx.reload(); } catch (e) { setErr(errText(e)); }
  }

  const conf = pending?.confidence ? CONF_STYLE[pending.confidence] : null;
  const shownMark = pending ? markText(pending) : markText(c.result);
  const excluded = pending ? exCells.has(pending.id) : false;
  const spotOpen = pending?.spot_check && !pending.spot_checked;
  return (
    <div class={'rounded-xl border bg-[var(--color-card)] p-2.5 flex flex-col gap-1.5 ' + (spotOpen ? 'border-[#6b8fd6]' : pending?.confidence === 'low' ? 'border-[#e3a0a0]' : 'border-[var(--color-border)]')}
      data-cell={item.label}>
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[13px] font-semibold text-[var(--color-text)] w-10">{item.label}</span>
        <span class="text-[13px] tabular-nums text-[var(--color-text)]"><b>{shownMark}</b>/{item.max_mark}</span>
        {conf && <Pill {...conf} />}
        {pending?.stale && <Pill label={`Out of date: ${pending.stale}`} bg="#f8cdcd" />}
        {pending?.teacher_changed && <Pill label="TEACHER CHANGED" bg="#fde9b8" />}
        {c.result && !pending && <Pill label="APPROVED" bg="#b9e6c4" />}
        {c.result && pending && <span class="text-[11px] text-[var(--color-text-muted)]">current: {markText(c.result)}</span>}
        {!pr && !c.result && <span class="text-[11.5px] text-[var(--color-text-faint)]">not marked</span>}
        {pending && (
          <label class="ml-auto text-[11.5px] text-[var(--color-text-muted)] flex items-center gap-1">
            <input type="checkbox" aria-label={`Exclude ${p.scan_number} ${item.label}`} checked={excluded}
              onChange={() => { const s = new Set(exCells); if (s.has(pending.id)) s.delete(pending.id); else s.add(pending.id); setExCells(s); }} />exclude
          </label>
        )}
      </div>
      {pr?.evidence && <div class="text-[12px] text-[var(--color-text-muted)]">Evidence: <span class="text-[var(--color-text)]">{pr.evidence}</span></div>}
      {pr && pr.codes.length > 0 && <div class="text-[12px] text-[var(--color-text-muted)]">Codes: <b class="text-[var(--color-text)]">{pr.codes.map((x) => ctx.codes.get(x) ?? x).join(', ')}</b></div>}
      {pending?.review_reason && <div class="text-[12px] text-[#b45309]">Review: {pending.review_reason}</div>}
      {pending && pending.flags.length > 0 && <div class="text-[11.5px] text-[#b42318]">Flags (advisory): {pending.flags.join(', ')}</div>}
      {pending && verified && (
        <div class="flex flex-wrap gap-2 items-center">
          {spotOpen && <Btn kind="primary" onClick={tick}>Tick spot check</Btn>}
          {pending.spot_checked && <Pill label="SPOT CHECKED" bg="#e8effb" />}
          {!pending.stale && !pending.teacher_changed && <Btn disabled={busy || bulkBlock(p, pending) === 'no usable mark'} onClick={() => send({ proposalIds: [pending.id] })}>Approve {item.label}</Btn>}
          {!pending.stale && pending.teacher_changed && <Btn kind="danger" disabled={busy} onClick={() => confirm('Replace the mark you entered with the AI proposal?') && send({ proposalIds: [pending.id], replaceTeacherDecision: [pending.id] })}>Replace teacher decision</Btn>}
          <Btn onClick={() => setEditing(!editing)}>{editing ? 'Cancel edit' : 'Edit'}</Btn>
        </div>
      )}
      {editing && (
        <div class="flex flex-wrap gap-2 items-center">
          <input aria-label={`Mark ${p.scan_number} ${item.label}`} class={inputCls + ' w-20'} value={text} onInput={(e) => setText(val(e))} />
          <span class="text-[11px] text-[var(--color-text-faint)]">b blank · u unreadable · m page missing</span>
          <Btn kind="primary" disabled={busy} onClick={saveEdit}>Save mark</Btn>
        </div>
      )}
      <ErrorLine error={err} />
    </div>
  );
}
